/**
 * Plan-based visibility and access utilities.
 * Access is managed by plan type (not plan ID). Multiple plans per user:
 * visible items are merged from all plan types.
 *
 * Plan types and access:
 * - freemium: Tender, Calendar, Apex Expert Connect (+ Result & BOQ on dashboard via applyFreemiumDashboardResultBoqAccess)
 * - enterprise: Tender, Result, BOQ Lead, Calendar, Apex Expert Connect
 * - pay_as_you_go: same as enterprise
 * - insights: enterprise + all Tender 360 views
 * - tender_bidding: Tender Bidding, Apex Expert Connect
 * - gem_consultancy: GeM Registration, Apex Expert Connect
 * - dsc: Dashboard not allowed
 */

const TENDER_ONLY = new Set(["tender", "calendar", "apex-expert-connect"]);
const TENDER_RESULT_BOQ = new Set(["tender", "result", "boq-lead", "calendar", "apex-expert-connect"]);
const TENDER_RESULT_BOQ_360 = new Set(["tender", "result", "boq-lead", "calendar", "apex-expert-connect", "tender360-dashboard", "tender360-bidder-profile", "tender360-competitor-matrix", "tender360-missed-opportunities", "tender360-lost-tender-evaluation", "tender360-rate-analysis", "tender360-department-insights", "tender360-state-statistics", "tender360-analytical-report"]);
const TENDER_BIDDING_ONLY = new Set(["tender-bidding", "apex-expert-connect"]);
const GEM_REGISTRATION = new Set(["gem-registration", "apex-expert-connect"]);

/** Views temporarily shown as locked and denied to every user, regardless of plan. */
const GLOBALLY_DISABLED_VIEWS = new Set(["tender-bidding"]);

export function isViewGloballyDisabled(viewId) {
  return GLOBALLY_DISABLED_VIEWS.has(viewId);
}

/** Plan types that are not allowed to access dashboard at all */
const NO_DASHBOARD_PLAN_TYPES = new Set(["dsc"]);

/** Valid plan types (normalized lowercase with underscores) */
export const PLAN_TYPES = Object.freeze([
  "enterprise",
  "tender_bidding",
  "freemium",
  "pay_as_you_go",
  "gem_consultancy",
  "dsc",
  "insights",
]);

function normalizePlanType(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim().toLowerCase().replace(/-/g, "_");
  if (s === "bidding") return "tender_bidding";
  return PLAN_TYPES.includes(s) ? s : null;
}

/**
 * True if any active plan from context is tender_bidding (API may send plantype "bidding").
 * @param {Array<{ plan_type?: string|null }>} plans
 * @returns {boolean}
 */
export function userHasTenderBiddingPlan(plans) {
  if (!Array.isArray(plans) || plans.length === 0) return false;
  return plans.some((p) => p.plan_type === "tender_bidding");
}

function getVisibleViewsForPlanType(planType) {
  const type = normalizePlanType(planType);
  if (!type) return new Set();
  if (type === "freemium") return new Set(TENDER_ONLY);
  if (type === "enterprise" || type === "pay_as_you_go") return new Set(TENDER_RESULT_BOQ);
  if (type === "insights") return new Set(TENDER_RESULT_BOQ_360);
  if (type === "tender_bidding") return new Set(TENDER_BIDDING_ONLY);
  if (type === "gem_consultancy") return new Set(GEM_REGISTRATION);
  if (type === "dsc") return new Set();
  return new Set();
}

/**
 * Derive plan_type from legacy plan_id when API does not send plan_type.
 * @param {number|null|undefined} planId
 * @returns {string|null}
 */
export function planTypeFromPlanId(planId) {
  const id = Number(planId);
  if (Number.isNaN(id)) return null;
  if (id === 1) return "freemium";
  if (id >= 2 && id <= 5) return "enterprise";
  if (id >= 6 && id <= 9) return "insights";
  if (id >= 10 && id <= 13) return "tender_bidding";
  if (id >= 14 && id <= 17) return "gem_consultancy";
  if (id >= 18 && id <= 21) return "dsc";
  return null;
}

/**
 * Returns a Set of sidebar view ids that should be visible for the given plan types.
 * Multiple plans are merged: user sees union of all views from all their plan types.
 * @param {string[]|string} planTypes - Single plan type or array of plan types
 * @returns {Set<string>}
 */
export function getVisibleSidebarItems(planTypes) {
  const types = Array.isArray(planTypes) ? planTypes : (planTypes != null ? [planTypes] : []);
  const visible = new Set();
  types.forEach((t) => {
    getVisibleViewsForPlanType(t).forEach((v) => visible.add(v));
  });
  return visible;
}

/**
 * Freemium-only accounts: allow Result + BOQ Lead on dashboard routes (PageAccessGate + sidebar).
 * Mixed plans (e.g. freemium + enterprise) keep the union from {@link getVisibleSidebarItems} only.
 * @param {string[]} planTypes - Normalized plan_type values from active plans
 * @param {Set<string>} visibleSidebarItems - Base visibility from getVisibleSidebarItems
 * @returns {Set<string>}
 */
export function applyFreemiumDashboardResultBoqAccess(planTypes, visibleSidebarItems) {
  if (!visibleSidebarItems || typeof visibleSidebarItems.has !== "function") {
    return visibleSidebarItems;
  }
  const types = (Array.isArray(planTypes) ? planTypes : [])
    .map((t) => normalizePlanType(t))
    .filter(Boolean);
  if (types.length === 0) return visibleSidebarItems;
  const onlyFreemium = types.every((t) => t === "freemium");
  if (!onlyFreemium) return visibleSidebarItems;
  const next = new Set(visibleSidebarItems);
  next.add("result");
  next.add("boq-lead");
  return next;
}

/**
 * Whether the user is allowed to open the dashboard.
 * If user has only "dsc" plan type, they cannot access dashboard.
 * @param {string[]|string} planTypes - Single plan type or array of plan types
 * @returns {boolean}
 */
export function canAccessDashboard(planTypes) {
  const types = Array.isArray(planTypes) ? planTypes : (planTypes != null ? [planTypes] : []);
  if (types.length === 0) return false;
  const onlyNoAccess = types.every((t) => NO_DASHBOARD_PLAN_TYPES.has(normalizePlanType(t)));
  return !onlyNoAccess;
}

/**
 * Parse zone from API (may be JSON string e.g. "[\"west\"]" or array).
 * @param {string|string[]|*} zone
 * @returns {string[]}
 */
function parseZone(zone) {
  if (zone == null) return [];
  if (Array.isArray(zone)) return zone.map((z) => String(z).trim()).filter(Boolean);
  if (typeof zone === "string") {
    const s = zone.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? parsed.map((z) => String(z).trim()).filter(Boolean) : [String(parsed).trim()];
    } catch (_) {
      return [s];
    }
  }
  return [String(zone).trim()].filter(Boolean);
}

/** API uses allindia / all-india as national coverage — not a state name and not a concrete zone option. */
export function isAllIndiaToken(value) {
  const t = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  return t === "allindia";
}

/** Plan zone/statetype `upto5` — pick up to 5 states from selectedstates, not a geographic zone. */
export function isUpto5Token(value) {
  const t = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  return t === "upto5";
}

/** Plan statetype `upto1` — use the single state stored in selectedstates. */
export function isUpto1Token(value) {
  const t = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  return t === "upto1";
}

function stripNonStatePlanTokensFromList(list) {
  return (list || [])
    .map((s) => String(s).trim())
    .filter(
      (s) =>
        s &&
        !isAllIndiaToken(s) &&
        !isUpto5Token(s) &&
        !isUpto1Token(s),
    );
}

function statetypeTokens(statetype) {
  if (statetype == null || statetype === "") return [];
  if (Array.isArray(statetype)) return statetype.map((s) => String(s).trim()).filter(Boolean);
  return String(statetype)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export const TENDER360_NO_PLAN_STATE = "__no_plan_state__";
export const EMPTY_TENDER360_STATE_SCOPE = Object.freeze({
  allIndia: false,
  restricted: false,
  states: Object.freeze([]),
  stateType: null,
  key: "unrestricted",
});

/**
 * Resolve Tender 360 geography from active Insights plans.
 * user_plans.statetype decides coverage; activation_form.selectedstates supplies
 * the concrete state names for upto1/upto5 plans.
 */
export function getTender360PlanStateScope(plans) {
  const insightPlans = (Array.isArray(plans) ? plans : []).filter(
    (plan) =>
      String(plan?.plan_type || "").trim().toLowerCase() === "insights",
  );
  if (insightPlans.length === 0) {
    return EMPTY_TENDER360_STATE_SCOPE;
  }

  const states = [];
  const seen = new Set();
  let allIndia = false;
  let restricted = false;
  let stateType = null;

  insightPlans.forEach((plan) => {
    const tokens = statetypeTokens(plan?.statetype);
    if (tokens.some(isAllIndiaToken)) {
      allIndia = true;
      stateType = "allindia";
      return;
    }

    const upto1 = tokens.some(isUpto1Token);
    const upto5 = tokens.some(isUpto5Token);
    const configuredStates = stripNonStatePlanTokensFromList(
      parseStatetypeToStates(plan?.selectedstates),
    );
    if (upto1 || upto5 || configuredStates.length > 0) restricted = true;
    if (upto1) stateType = "upto1";
    else if (upto5 && stateType !== "upto1") stateType = "upto5";

    const limit = upto1 ? 1 : upto5 ? 5 : configuredStates.length;
    configuredStates.slice(0, limit).forEach((state) => {
      const key = state.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      states.push(state);
    });
  });

  if (allIndia) {
    return {
      allIndia: true,
      restricted: false,
      states: [],
      stateType: "allindia",
      key: "allindia",
    };
  }

  return {
    allIndia: false,
    restricted,
    states,
    stateType,
    key: restricted ? `restricted:${states.join("|")}` : "unrestricted",
  };
}

/** Apply the plan state boundary to a page's current state selection. */
export function getEffectiveTender360States(selectedStates, stateScope) {
  const selected = (Array.isArray(selectedStates)
    ? selectedStates
    : selectedStates == null || selectedStates === ""
      ? []
      : [selectedStates]
  )
    .map((state) => String(state ?? "").trim())
    .filter(Boolean);

  if (!stateScope?.restricted) return [...new Set(selected)];
  const allowed = new Map(
    (stateScope.states || []).map((state) => [state.toLowerCase(), state]),
  );
  const valid = selected
    .map((state) => allowed.get(state.toLowerCase()))
    .filter(Boolean);
  if (valid.length > 0) return [...new Set(valid)];
  return stateScope.states?.length
    ? [...stateScope.states]
    : [TENDER360_NO_PLAN_STATE];
}

function planHasUpto5Coverage(p) {
  if (!p) return false;
  if (parseZone(p.zone).some((z) => isUpto5Token(z))) return true;
  return statetypeTokens(p.statetype).some((t) => isUpto5Token(t));
}

/**
 * Merge selectedcategories and zones from all plans into one set of values.
 * Used to pre-fill and lock category/zone filters on dashboard pages.
 * @param {Array<{ selectedcategories?: string[], selectedstates?: string[], zone?: string|string[] }>} plans
 * @returns {{ selectedcategories: string[], selectedstates: string[], zones: string[], allIndiaZoneCoverage: boolean, upto5StateSelection: boolean, upto5EligibleStates: string[] }}
 */
export function mergePlanFilters(plans) {
  const categories = new Set();
  const states = new Set();
  const upto5EligibleStates = new Set();
  const zones = new Set();
  let allIndiaZoneCoverage = false;
  let upto5StateSelection = false;
  (plans || []).forEach((p) => {
    (p.selectedcategories || []).forEach((c) => categories.add(c));
    (p.selectedstates || []).forEach((s) => {
      if (!isAllIndiaToken(s) && !isUpto5Token(s)) states.add(s);
    });
    parseZone(p.zone).forEach((z) => {
      const zt = String(z).trim();
      if (!zt) return;
      if (isAllIndiaToken(zt)) allIndiaZoneCoverage = true;
      else if (isUpto5Token(zt)) upto5StateSelection = true;
      else zones.add(zt);
    });
    statetypeTokens(p.statetype).forEach((part) => {
      if (isAllIndiaToken(part)) allIndiaZoneCoverage = true;
      if (isUpto5Token(part)) upto5StateSelection = true;
    });
    if (planHasUpto5Coverage(p)) {
      (p.selectedstates || []).forEach((s) => {
        if (!isAllIndiaToken(s) && !isUpto5Token(s)) upto5EligibleStates.add(String(s).trim());
      });
    }
  });
  const upto5Pool = Array.from(upto5EligibleStates);
  const zonesList = Array.from(zones).filter(
    (z) => !isUpto5Token(z) && !isAllIndiaToken(z),
  );
  const tender360StateScope = getTender360PlanStateScope(plans);
  return {
    selectedcategories: Array.from(categories),
    selectedstates: Array.from(states),
    zones: zonesList,
    allIndiaZoneCoverage,
    upto5StateSelection,
    upto5EligibleStates: upto5Pool.length > 0 ? upto5Pool : [],
    tender360StateScope,
  };
}

/**
 * Normalize API response (login or user-active-plan-details) to a common shape.
 * Plans are normalized with plan_type (from API or derived from plan_id).
 * Supports: plan_type / plantype from API, or legacy plan_id / pId for derivation.
 * @param {Object} response - { plans: [...] }
 * @returns {{ plans: Array<{ plan_id?: number, plan_type: string, selectedcategories?: string[], selectedstates?: string[], ... }> }}
 */
function parseStatetypeToStates(statetype) {
  if (statetype == null || statetype === "") return [];
  if (Array.isArray(statetype)) return statetype.map((s) => String(s).trim()).filter(Boolean);
  const raw = String(statetype).trim();
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((s) => String(s).trim()).filter(Boolean);
    }
  } catch (_) {
    // Plain comma-separated values are handled below.
  }
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function normalizePlanDetails(response) {
  const rawPlans = Array.isArray(response?.plans)
    ? response.plans
    : response && (response.plan_id != null || response.pId != null || response.plan_type != null || response.plantype != null)
      ? [response]
      : [];
  const plans = rawPlans.map((p) => {
    const planId = p.pId != null ? Number(p.pId) : (p.plan_id != null ? Number(p.plan_id) : null);
    const planType =
      normalizePlanType(p.plan_type ?? p.plantype) ?? planTypeFromPlanId(planId);
    const fromApiStates = stripNonStatePlanTokensFromList(
      parseStatetypeToStates(p.selectedstates),
    );
    const fromStatetype =
      fromApiStates.length > 0
        ? []
        : stripNonStatePlanTokensFromList(parseStatetypeToStates(p.statetype));
    const selectedstates = fromApiStates.length > 0 ? fromApiStates : fromStatetype;
    return {
      plan_id: planId,
      plan_type: planType,
      selectedcategories: Array.isArray(p.selectedcategories) ? p.selectedcategories : [],
      selectedstates,
      uId: p.uId != null ? Number(p.uId) : p.uId,
      startdate: p.startdate ?? null,
      enddate: p.enddate ?? p.plan_end_date ?? null,
      statetype: p.statetype ?? null,
      zone: parseZone(p.zone),
      servicecount: p.servicecount != null ? Number(p.servicecount) : 0,
      isactive: p.isactive != null ? Number(p.isactive) : 0,
      activeform: p.activeform != null ? Number(p.activeform) : 0,
      paymentid: p.paymentid != null ? Number(p.paymentid) : 0,
    };
  }).filter((p) => p.plan_type != null);
  return { plans };
}
